export interface RealtimeDecryptor {
  decrypt(data: unknown): Promise<unknown>
}

interface EncryptedEnvelope {
  v: number
  ct: string
}

function isEncryptedEnvelope(data: unknown): data is EncryptedEnvelope {
  if (typeof data !== 'object' || data === null) {
    return false
  }
  const candidate = data as Record<string, unknown>

  return typeof candidate.v === 'number' && typeof candidate.ct === 'string'
}

// Return type is intentionally inferred as `Uint8Array<ArrayBuffer>` (not the wider
// `Uint8Array<ArrayBufferLike>`): TypeScript 5.7+ made typed arrays generic, and
// WebCrypto's importKey/decrypt require an ArrayBuffer-backed `BufferSource`.
function base64ToBytes(value: string) {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes
}

export class AesGcmDecryptor implements RealtimeDecryptor {
  private readonly keyPromise: Promise<CryptoKey>

  constructor(keyBase64: string) {
    this.keyPromise = crypto.subtle.importKey(
      'raw',
      base64ToBytes(keyBase64),
      { name: 'AES-GCM' },
      false,
      ['decrypt'],
    )
  }

  async decrypt(data: unknown): Promise<unknown> {
    if (!isEncryptedEnvelope(data)) {
      return data
    }

    const [ivBase64, tagBase64, cipherBase64] = data.ct.split(':')
    if (!ivBase64 || !tagBase64 || !cipherBase64) {
      throw new Error('Malformed realtime encrypted payload.')
    }

    const iv = base64ToBytes(ivBase64)
    const cipherText = base64ToBytes(cipherBase64)
    const tag = base64ToBytes(tagBase64)

    const combined = new Uint8Array(cipherText.length + tag.length)
    combined.set(cipherText)
    combined.set(tag, cipherText.length)

    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      await this.keyPromise,
      combined,
    )

    return JSON.parse(new TextDecoder().decode(plain))
  }
}
